//@ts-strict-ignore
import React, { ReactElement, useContext, useEffect, useState } from 'react';
import ModalContext from '../../components/Context/ModalContext';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { useDispatch } from 'react-redux';
import {
  AlertType,
  FilterOperation,
  IProduct,
  IProductInput,
  IProductWarehouseDetail,
  IPurchaseRequirementBySupplierQueryResult,
  IPurchaseRequirementBySupplierQueryVariables,
  IPurchaseRequirementDetailInput,
  IPurchaseRequirementInput,
  IPurchaseRequirementUpdateMutationResult,
  IPurchaseRequirementUpdateMutationVariables,
  ISupplier,
  ISupplierInput,
  IWarehouse,
  IWarehouseInput,
  Maybe,
  ShortageOrigin,
} from '../../graphql/schema/p4types';
import {
  SupplierOption,
  useSupplierOptions,
} from '../../components/modals/AlternativeProductModal/RaiseShortageSection';
import { useMutation, useQuery } from '@apollo/client';
import PurchaseRequirementBySupplierIdQuery from '../../graphql/queries/purchases/PurchaseRequirementBySupplierIdQuery.graphql';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import PurchaseRequirementMutation from '../../graphql/mutations/purchases/PurchaseRequirementMutation.graphql';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import getClientMutationId from '../../utils/getClientMutationId';
import { BasicHeader } from '../BasicHeader';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import Dropdown from '../../components/connectedForm/Dropdown';
import { ModalBottomButtonContainer } from '../../components/modals/Styles';
import { IDropdownOptionRenderer, IFormAction } from '../../@types/entityTypes';
import { ProductFormAction } from './ProductFormAction';
import { PrimaryButton, SecondaryButton } from '../../components/connectedForm/Button';
import BlurTextField from '../../components/connectedForm/BlurTextField';
import { Warehouse } from '../../graphql/entities';
import { serviceActions } from '../../state/modules/service';
import Fieldset from '../../components/connectedForm/Fieldset';
import Row from '../../components/connectedForm/Row';
import UserContext from '../../components/Context/UserContext';
import { DescriptionPill, Option } from '../../components/DropdownEditor/CustomOptions';
import Supplier from '../../graphql/entities/supplier/Supplier';
import FluentModal from '../../components/modals/FluentModal';

const LazyPurchaseRequirementDetailWorkspace = React.lazy(
  () => import('../../views/purchases/PurchaseRequirementDetailWorkspace')
);

export const SupplierValue: IDropdownOptionRenderer = ({
  option: { value, supplier: { name, reference } = {} },
}: {
  option: {
    value: any;
    supplier: { name?: string; reference?: string };
  };
}) => {
  const referencePill = reference ? <DescriptionPill>{reference}</DescriptionPill> : null;
  return (
    <Option>
      {name || value} {referencePill}
    </Option>
  );
};

const AddProductToShortageModalContent = () => {
  const { onClose } = useContext(ModalContext);
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();
  const userContext = useContext(UserContext);

  const product: IProduct = connectedForm.values as IProduct;
  const [warehouse, setWarehouse] = useState<Maybe<IWarehouse>>(userContext.currentUser.companyUser.purchaseWarehouse);
  const productMainSupplier: Maybe<ISupplier> =
    product.alternateSupplierDetails?.find((asd) => asd?.mainSupplier)?.supplier ||
    product.alternateSupplierDetails?.[0]?.supplier ||
    null;
  const [supplierState, setSupplierState] = useState<{ supplier: Maybe<ISupplier>; choice: number | 'OTHER' | null }>({
    supplier: productMainSupplier,
    choice: productMainSupplier?.id || null,
  });
  const [quantityToOrder, setQuantityToOrder] = useState<number>(0);
  const [comment, setComment] = useState<string>('');

  useEffect(() => {
    if (supplierState.supplier) {
      const productSupplierDetail = product.alternateSupplierDetails?.find(
        (asd) => asd?.supplier?.id === supplierState.supplier!.id
      );
      if (productSupplierDetail?.orderQuantityMinimum > 0) {
        setQuantityToOrder(productSupplierDetail!.orderQuantityMinimum);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierState.supplier]);

  const productWarehouseDetail: Maybe<IProductWarehouseDetail> = product.productWarehouseDetails?.find(
    (pwd: IProductWarehouseDetail) => pwd.warehouse?.id === warehouse?.id
  );

  const supplierOptions = useSupplierOptions({ product });

  const { data: purchaseRequirementResult, refetch: refetchPurchaseRequirementDetail } = useQuery<
    IPurchaseRequirementBySupplierQueryResult,
    IPurchaseRequirementBySupplierQueryVariables
  >(PurchaseRequirementBySupplierIdQuery, {
    variables: {
      supplierId: supplierState.supplier?.id,
    },
  });

  const handleQueryError = useComponentQueryErrorHandling();
  const [savePurchaseRequirementDetail] = useMutation<
    IPurchaseRequirementUpdateMutationResult,
    IPurchaseRequirementUpdateMutationVariables
  >(PurchaseRequirementMutation, {
    onCompleted: () => {
      dispatch(serviceActions.genericAlert('Shortage Generation', 'Shortage has been generated', AlertType.Success));
      onClose?.();
    },
    onError: handleQueryError,
  });

  const onClickAddToShortages = () => {
    const updatedPurchaseRequirement: IPurchaseRequirementInput = (normalizeVariables<any, IPurchaseRequirementInput>(
      purchaseRequirementResult?.data
    ) || {
      supplier: normalizeVariables<Maybe<ISupplier>, ISupplierInput>(supplierState.supplier),
      purchaseRequirementDetails: [],
    }) as IPurchaseRequirementInput;

    if (updatedPurchaseRequirement.supplier?.id !== supplierState.supplier?.id) {
      dispatch(serviceActions.genericAlert('Validation', 'Supplier mismatch, could not generate shortage.'));
      return;
    } else if (!product || !warehouse) {
      dispatch(serviceActions.genericAlert('Validation', 'Product and Warehouse are required fields'));
      return;
    }

    const newPurchaseRequirementDetail: IPurchaseRequirementDetailInput = {
      product: normalizeVariables<IProduct, IProductInput>(product),
      warehouse: normalizeVariables<IWarehouse, IWarehouseInput>(warehouse),
      requiredQuantity: quantityToOrder,
      shortageOrigin: ShortageOrigin.RestockManual,
      comment: comment,
    };
    updatedPurchaseRequirement.purchaseRequirementDetails?.push(newPurchaseRequirementDetail);
    savePurchaseRequirementDetail({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          purchaseRequirement: updatedPurchaseRequirement,
        },
      },
    }).then(() => {
      //Reload purchase requirement detail so if user attempts to add multiple shortages in a singls session, we don't get a concurrency conflict.
      refetchPurchaseRequirementDetail();
    });
  };

  return (
    <>
      <BasicHeader secondaryTitle={product.description || undefined}>
        Add to Shortages ({product.reference})
      </BasicHeader>
      <div style={{ padding: '1em' }}>
        <QueryDropdown
          label={'Warehouse'}
          value={warehouse}
          onChange={setWarehouse}
          {...Warehouse.dropdown}
          data-testid={'add-to-shortages-warehouse'}
        />
        <BlurTextField
          label={'Free Stock'}
          readOnly={true}
          value={productWarehouseDetail?.freeStock}
          data-testid={'add-to-shortages-free-stock'}
        />
        <BlurTextField
          label={'On Order'}
          readOnly={true}
          value={productWarehouseDetail?.onOrder}
          data-testid={'add-to-shortages-on-order'}
        />
        <Row style={{ marginLeft: 0, marginRight: 0 }}>
          <Fieldset cols={1 / 3} style={{ paddingLeft: 0 }}>
            <BlurTextField
              label={'Minimum Level'}
              readOnly={true}
              value={productWarehouseDetail?.minimumLevel}
              inputCols={1 / 2}
              labelCols={1 / 2}
              data-testid={'add-to-shortages-minimum-level'}
            />
          </Fieldset>
          <Fieldset cols={1 / 3}>
            <BlurTextField
              label={'Optimum Level'}
              readOnly={true}
              value={productWarehouseDetail?.optimumLevel}
              inputCols={1 / 2}
              labelCols={1 / 2}
              data-testid={'add-to-shortages-optimum-level'}
            />
          </Fieldset>
          <Fieldset cols={1 / 3}>
            <BlurTextField
              label={'Maximum Level'}
              readOnly={true}
              value={productWarehouseDetail?.maximumLevel}
              inputCols={1 / 2}
              labelCols={1 / 2}
              data-testid={'add-to-shortages-maximum-level'}
            />
          </Fieldset>
        </Row>
        <Dropdown
          options={supplierOptions.filter((option) => option.key !== 'LATER')}
          optionRenderer={SupplierOption}
          valueRenderer={SupplierValue}
          label="Supplier"
          valueKey="key"
          labelKey="value"
          onChange={(value) => {
            setSupplierState({
              supplier: value.supplier || null,
              choice: value.key,
            });
          }}
          value={supplierState.choice}
        />
        {supplierState.choice === 'OTHER' ? (
          <QueryDropdown
            {...Supplier.dropdown}
            label="Other Supplier"
            valueKey=""
            labelKey="name"
            onChange={(value) => {
              setSupplierState({
                supplier: value,
                choice: supplierState.choice,
              });
            }}
            value={supplierState.supplier}
            data-testid={'other-supplier'}
            preLoad={true}
          />
        ) : null}
        <BlurTextField
          label={'Quantity to Order'}
          value={quantityToOrder}
          enableOnChange={true}
          onChange={setQuantityToOrder}
          data-testid={'add-to-shortages-quantity-to-order'}
        />
        <BlurTextField
          label={'Comment'}
          value={comment}
          enableOnChange={true}
          onChange={setComment}
          data-testid={'add-to-shortages-comment'}
        />
        <LazyPurchaseRequirementDetailWorkspace
          embeddedView={true}
          predefinedFilters={[
            supplierState
              ? {
                  filterOperation: FilterOperation.Equals,
                  filterParameters: { field: 'purchaseRequirement.supplier.id', value: supplierState.supplier?.id },
                }
              : null,
            product
              ? {
                  filterOperation: FilterOperation.Equals,
                  filterParameters: { field: 'product.id', value: product.id },
                }
              : null,
            warehouse
              ? {
                  filterOperation: FilterOperation.Equals,
                  filterParameters: { field: 'warehouse.id', value: warehouse.id },
                }
              : null,
          ].filter((filter) => !!filter)}
          overrideParams={{ id: purchaseRequirementResult?.data?.id }}
          contentWidth={700}
          contentHeight={250}
          overrideFieldNames={[
            'shortageOrigin',
            'requiredDate',
            'requiredQuantity',
            'lineValue',
            'orderQuantityMinimum',
            'approved',
          ]}
          viewScreenName={'/Products/Product/Details/AddToShortagesPreview'}
        />
        <ModalBottomButtonContainer>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={onClickAddToShortages}>Add to Shortages</PrimaryButton>
        </ModalBottomButtonContainer>
      </div>
    </>
  );
};
export const useProductShortageAction: () => [IFormAction<ProductFormAction>, ReactElement] = () => {
  const [modalOpen, setModalOpen] = useState(false);
  //Used to defer rendering until user actually needs the feature (I.e. Avoiding wasting time loading unnecessary stuff).
  const [renderContent, setRenderContent] = useState(false);

  return [
    {
      key: 'addToShortageList',
      text: 'Add To Shortages',
      onClick: () => {
        setModalOpen(true);
        setRenderContent(true);
      },
      actionReference: ProductFormAction.AddToShortageList,
    },
    <ModalContext.Provider value={{ onClose: () => setModalOpen(false) }}>
      <FluentModal
        isOpen={modalOpen}
        styles={{ main: { minWidth: '830px', paddingBottom: '60px' }, scrollableContent: { maxHeight: '97vh' } }}
      >
        {renderContent ? <AddProductToShortageModalContent /> : null}
      </FluentModal>
    </ModalContext.Provider>,
  ];
};
